import { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { Loading, Markdown } from "@lopie/kernel-ui";
import { api, PRIORITIES, SIZES } from "../lib/projects-api.ts";
import DoneToggle from "./DoneToggle.jsx";

/**
 * One task, opened from a card. Editing a field saves on blur; the assignee,
 * priority, size and epic are pickers rather than free text so the values stay
 * the ones the board understands. Assignees come from the kernel directory
 * passed in, never a list this app keeps.
 *
 * Below the fields are the three things a task accretes as it is worked:
 * **tags** (chosen from the project's own palette, or typed to make a new
 * one), **subtasks** (the checklist a card breaks into — child tasks in the
 * same project), and **followers** (people keeping it in their sightline),
 * then the comment thread.
 */
export default function TaskDrawer({
  project, task, people, epics, sections = [], tags = [], tasks = [], me, readOnly = false,
  onClose, onChanged,
}) {
  const [draft, setDraft] = useState(task);
  const [comments, setComments] = useState(null);
  const [comment, setComment] = useState("");
  const [followers, setFollowers] = useState(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    api.comments(task.id).then((r) => setComments(r.items)).catch(() => setComments([]));
    api.followers(task.id).then((r) => setFollowers(r.items)).catch(() => setFollowers([]));
  }, [task.id]);

  const subtasks = tasks.filter((t) => t.parentTaskId === task.id);
  const following = (followers || []).some((f) => f.userId === me);

  const save = async (fields) => {
    const next = { ...draft, ...fields };
    setDraft(next);
    await api.updateTask(project.id, task.id, fields);
    setDirty(true);
  };

  const postComment = async () => {
    if (!comment.trim()) return;
    // projectId lets the server find the task to email its followers.
    const created = await api.addComment(task.id, { body: comment, projectId: project.id });
    setComments([...(comments || []), created]);
    setComment("");
  };

  const toggleFollow = async () => {
    if (following) {
      await api.unfollow(task.id, me);
      setFollowers((followers || []).filter((f) => f.userId !== me));
    } else {
      const created = await api.follow(task.id, project.id);
      setFollowers([...(followers || []), created]);
    }
  };

  const close = () => { if (dirty) onChanged(); else onClose(); };

  const remove = async () => {
    if (!window.confirm("Delete this task?")) return;
    await api.deleteTask(project.id, task.id);
    onChanged();
  };

  const tagColor = (name) => tags.find((t) => t.name === name)?.color;

  return (
    <Drawer anchor="right" open onClose={close}
      slotProps={{ paper: { sx: { width: { xs: "100%", sm: 480 }, p: 2 } } }}>
      <Stack spacing={2}>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace", flex: 1 }}>
            {project.key}-{task.number}
          </Typography>
          {!readOnly ? <Button size="small" color="error" onClick={remove}>Delete</Button> : null}
          <IconButton size="small" onClick={close} aria-label="Close">
            <Box component="span" sx={{ fontSize: 18, lineHeight: 1 }}>×</Box>
          </IconButton>
        </Stack>

        {/* Independent of which column the card sits in — done is a fact
            about the work, not about where it is parked on the board. */}
        <DoneToggle done={draft.status === "done"} disabled={readOnly}
          onChange={(done) => save({ status: done ? "done" : "open" })} />

        <TextField label="Title" defaultValue={draft.title} fullWidth disabled={readOnly}
          onBlur={(e) => e.target.value.trim() && e.target.value !== draft.title && save({ title: e.target.value })} />

        <TextField label="Description" defaultValue={draft.description || ""} fullWidth multiline minRows={3} disabled={readOnly}
          onBlur={(e) => e.target.value !== (draft.description || "") && save({ description: e.target.value })} />

        <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap" }}>
          <TextField select label="Assignee" value={draft.assigneeId || ""} sx={{ minWidth: 180, flex: 1 }} disabled={readOnly}
            onChange={(e) => save({ assigneeId: e.target.value })}>
            <MenuItem value="">Unassigned</MenuItem>
            {people.map((p) => (
              <MenuItem key={p.id} value={p.id}>{p.displayName || p.emails?.[0] || p.id}</MenuItem>
            ))}
          </TextField>
          <TextField select label="Epic" value={draft.epicId || ""} sx={{ minWidth: 160, flex: 1 }} disabled={readOnly}
            onChange={(e) => save({ epicId: e.target.value })}>
            <MenuItem value="">None</MenuItem>
            {epics.map((ep) => <MenuItem key={ep.id} value={ep.id}>{ep.name}</MenuItem>)}
          </TextField>
        </Stack>

        {/* Moving a card between columns by dragging is mouse-only; this is
            the keyboard way to do the same thing. */}
        {sections.length ? (
          <TextField select label="Column" value={draft.sectionId || ""} fullWidth disabled={readOnly}
            onChange={(e) => save({ sectionId: e.target.value })}>
            {sections.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
          </TextField>
        ) : null}

        <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap" }}>
          <TextField select label="Priority" value={draft.priority || ""} sx={{ minWidth: 120, flex: 1 }} disabled={readOnly}
            onChange={(e) => save({ priority: e.target.value })}>
            <MenuItem value="">None</MenuItem>
            {PRIORITIES.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
          </TextField>
          <TextField select label="Size" value={draft.tshirtSize || ""} sx={{ minWidth: 100, flex: 1 }} disabled={readOnly}
            onChange={(e) => save({ tshirtSize: e.target.value })}>
            <MenuItem value="">None</MenuItem>
            {SIZES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </TextField>
          <TextField type="date" label="Due" slotProps={{ inputLabel: { shrink: true } }} disabled={readOnly}
            defaultValue={draft.dueDate || ""} sx={{ minWidth: 150, flex: 1 }}
            onBlur={(e) => e.target.value !== (draft.dueDate || "") && save({ dueDate: e.target.value })} />
        </Stack>

        {/* Tags: free solo, so a name the palette does not have yet is typed
            once and becomes one. The chip colour is the palette's when it
            knows the name and default otherwise, so a tag keeps working after
            its catalogue entry is deleted. */}
        <Autocomplete multiple freeSolo size="small" disabled={readOnly}
          options={tags.map((t) => t.name)}
          value={draft.tags || []}
          onChange={(_, value) => {
            const names = value.map(String);
            // A name the catalogue does not carry yet is added to it, so it is
            // offered on the next card and can be given a colour later. Best
            // effort: the tag still lives on the task even if this fails.
            const known = new Set(tags.map((t) => t.name));
            names.filter((n) => !known.has(n)).forEach((n) => {
              api.createTag(project.id, { name: n }).catch(() => {});
            });
            save({ tags: names });
          }}
          renderTags={(value, getTagProps) =>
            value.map((name, i) => (
              <Chip size="small" label={name} variant="outlined"
                sx={{ borderColor: tagColor(name) || "divider" }}
                {...getTagProps({ index: i })} key={name} />
            ))}
          renderInput={(params) => <TextField {...params} label="Tags" placeholder="Add a tag" />} />

        <Divider />
        <SubtaskSection project={project} task={task} subtasks={subtasks} readOnly={readOnly}
          onChanged={() => { setDirty(true); }} onReloadNeeded={onChanged} />

        <Divider />
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <Typography variant="subtitle2" sx={{ flex: 1 }}>
            Followers{followers ? ` · ${followers.length}` : ""}
          </Typography>
          {followers ? (
            <Button size="small" variant={following ? "outlined" : "contained"} onClick={toggleFollow}>
              {following ? "Following" : "Follow"}
            </Button>
          ) : null}
        </Stack>
        <Typography variant="caption" color="text.secondary">
          Following keeps a task in your My tasks list and emails you when
          someone comments on it.
        </Typography>

        <Divider />
        <Typography variant="subtitle2">Comments</Typography>
        {comments == null ? <Loading label="Reading comments" minHeight={40} size={18} /> : (
          <Stack spacing={1.5}>
            {comments.map((c) => (
              <Box key={c.id}>
                <Typography variant="caption" color="text.secondary">
                  {c.authorName || c.authorId} · {new Date(c.createdAt).toLocaleString()}
                </Typography>
                <Box sx={{ "& p": { my: 0.5 }, "& :first-of-type": { mt: 0 }, "& :last-child": { mb: 0 } }}>
                  <Markdown>{c.body}</Markdown>
                </Box>
              </Box>
            ))}
            {!comments.length ? <Typography variant="body2" color="text.secondary">No comments yet.</Typography> : null}
          </Stack>
        )}
        <Stack direction="row" spacing={1} sx={{ alignItems: "flex-end" }}>
          <TextField size="small" placeholder="Add a comment" value={comment} fullWidth multiline maxRows={4}
            onChange={(e) => setComment(e.target.value)} />
          <Button variant="contained" onClick={postComment} disabled={!comment.trim()}>Post</Button>
        </Stack>
      </Stack>
    </Drawer>
  );
}

/** The checklist a card breaks into: child tasks in the same project and
 *  column, shown as a small list with an add box. Completing one is deleting
 *  it for the MVP — there is no done state on a task yet, and a subtask that
 *  is finished is one off the list. */
function SubtaskSection({ project, task, subtasks, readOnly = false, onChanged, onReloadNeeded }) {
  const [title, setTitle] = useState("");
  const add = async () => {
    if (!title.trim()) return;
    await api.createTask(project.id, {
      title, sectionId: task.sectionId, parentTaskId: task.id,
    });
    setTitle("");
    onReloadNeeded();
  };
  const done = async (id) => {
    await api.deleteTask(project.id, id);
    onReloadNeeded();
  };
  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2">Subtasks{subtasks.length ? ` · ${subtasks.length}` : ""}</Typography>
      {subtasks.map((s) => (
        <Stack key={s.id} direction="row" spacing={1} sx={{ alignItems: "center" }}>
          {!readOnly ? (
            <Button size="small" variant="outlined" sx={{ minWidth: 0, px: 1 }}
              onClick={() => done(s.id)} aria-label="Complete subtask">✓</Button>
          ) : null}
          <Typography variant="body2" sx={{ flex: 1 }}>{s.title}</Typography>
        </Stack>
      ))}
      {!readOnly ? (
        <Stack direction="row" spacing={1}>
          <TextField size="small" placeholder="Add a subtask" value={title} fullWidth
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") add(); }} />
          <Button size="small" onClick={add} disabled={!title.trim()}>Add</Button>
        </Stack>
      ) : null}
    </Stack>
  );
}
